// @ts-check
import { computed } from 'vue';

/**
 * Retrieves the computed style property value of a given element.
 *
 * @param {HTMLElement} element - The element to retrieve the style property from.
 * @param {string} prop - The name of the style property to retrieve.
 * @returns {string} The computed value of the specified style property.
 */
function getStyleProperty(element, prop) {
  return getComputedStyle(element, null).getPropertyValue(prop);
}

/**
 * Returns the combined overflow property of a given DOM node.
 *
 * @param {HTMLElement} element - The DOM node to get the overflow property from.
 * @returns {string} The combined overflow property of the DOM node.
 */
function getOverflow(element) {
  return (
    getStyleProperty(element, 'overflow') +
    getStyleProperty(element, 'overflow-y') +
    getStyleProperty(element, 'overflow-x')
  );
}

/**
 * Determines if the given node is scrollable.
 *
 * @param {HTMLElement} element - The node to check.
 * @returns {boolean} - True if the node is scrollable, false otherwise.
 */
function isScrollable(element) {
  return /(scroll|auto)/.test(getOverflow(element));
}

/**
 * Returns an array of all parent nodes of the given node.
 *
 * @param {HTMLElement} element - The element for which to find parents.
 * @returns {Array<HTMLElement>} - An array of all parent elements of the given element.
 */
function getParents(element) {
  if (element.parentElement === null) {
    return [];
  }

  return getParents(element.parentElement).concat([element]);
}

/**
 * Finds the nearest parent of the given node that is scrollable.
 *
 * @param {HTMLElement|undefined} element - The element for which to find the scroll parent.
 * @returns {HTMLElement} - The scroll parent of the given element, or 'document' if not found.
 */
function getScrollParent(element) {
  if (!element || !element.parentElement) {
    return document.documentElement;
  }

  const parents = getParents(element);

  const scrollElement = parents.find(isScrollable);

  return scrollElement || document.documentElement;
}

/** @param {import('vue').Ref<HTMLElement|undefined>} element */
export const useScrollParent = element =>
  computed(() => getScrollParent(element.value));
